import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Department } from '@/types/project';
import { getAccessToken } from '@/utils/jwt';

interface CreateDepartmentBody {
  name: string;
  code: string;
  description?: string;
  sort_order?: number;
}

interface UpdateDepartmentBody {
  id: string;
  name?: string;
  code?: string;
  description?: string | null;
  sort_order?: number;
}

export const departmentsApi = createApi({
  reducerPath: 'departmentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Department'],
  endpoints: (builder) => ({
    listDepartments: builder.query<Department[], undefined>({
      query: () => '/departments',
      transformResponse: (response: { success: boolean; departments: Department[] }) =>
        response.departments,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Department' as const, id })),
              { type: 'Department', id: 'LIST' },
            ]
          : [{ type: 'Department', id: 'LIST' }],
    }),
    getDepartment: builder.query<Department, string>({
      query: (id) => `/departments/${id}`,
      transformResponse: (response: { success: boolean; department: Department }) =>
        response.department,
      providesTags: (_r, _e, id) => [{ type: 'Department', id }],
    }),
    createDepartment: builder.mutation<Department, CreateDepartmentBody>({
      query: (body) => ({ url: '/departments', method: 'POST', body }),
      transformResponse: (response: { success: boolean; department: Department }) =>
        response.department,
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),
    updateDepartment: builder.mutation<Department, UpdateDepartmentBody>({
      query: ({ id, ...body }) => ({ url: `/departments/${id}`, method: 'PUT', body }),
      transformResponse: (response: { success: boolean; department: Department }) =>
        response.department,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Department', id },
        { type: 'Department', id: 'LIST' },
      ],
    }),
    deleteDepartment: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/departments/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),
    reorderDepartments: builder.mutation<Department[], { department_ids: string[] }>({
      query: (body) => ({ url: '/departments/reorder', method: 'PUT', body }),
      transformResponse: (response: { success: boolean; departments: Department[] }) =>
        response.departments,
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),
  }),
});

export const {
  useListDepartmentsQuery,
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useReorderDepartmentsMutation,
} = departmentsApi;
